/* eslint-disable */

import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import AppShell from "../components/AppShell";
import MemberAvatar from "../components/MemberAvatar";
import PostImagePicker from "../components/PostImagePicker";
import PostImages from "../components/PostImages";
import ZipPromptBanner from "../components/ZipPromptBanner";
import HoursLocationLink from "../components/HoursLocationLink";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import { toast, Toaster } from "sonner";
import { linkify } from "../lib/linkify";
import { firstName as _firstName, displayName as _displayName } from "../lib/displayName";
import { openExternal } from "../lib/externalLink";

const HeartIcon = ({ filled }) => (
  <svg className="m-icon" viewBox="0 0 24 24" fill={filled ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
  </svg>
);
const CommentIcon = () => (
  <svg className="m-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
  </svg>
);

function timeAgo(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const diff = Date.now() - then;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

export default function Community() {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [events, setEvents] = useState([]);
  const [members, setMembers] = useState([]);
  const [composer, setComposer] = useState("");
  const [composerImageIds, setComposerImageIds] = useState([]);
  const [composerImagePreviews, setComposerImagePreviews] = useState([]);
  const [posting, setPosting] = useState(false);
  // Session 32 — post edit/delete state
  const [openMenu, setOpenMenu] = useState(null);
  const [editingPostId, setEditingPostId] = useState(null);
  const [editBody, setEditBody] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);
  // Session 59.3 · P0 · Community comments — was skeleton-only before this.
  const [openCommentPostId, setOpenCommentPostId] = useState(null);
  const [commentsByPost, setCommentsByPost] = useState({});
  const [commentDraft, setCommentDraft] = useState({});
  const [commentBusy, setCommentBusy] = useState({});
  // Session 60.7 · Notification deep-link — when the URL carries
  // `?post=<pid>`, we surface that post at the top of the feed even if it
  // is older than the first 200 loaded. `?comment=1` also auto-opens the
  // comment thread so the recipient lands on the exact activity that
  // triggered the notification.
  const [searchParams] = useSearchParams();
  const targetPostId = searchParams.get("post") || null;
  const focusComment = searchParams.get("comment") === "1";
  const [targetMissing, setTargetMissing] = useState(false);
  const targetPostRef = useRef(null);

  const load = async () => {
    try {
      const [p, e, m] = await Promise.all([
        api.get("/posts"), api.get("/events"), api.get("/members"),
      ]);
      setPosts(p.data); setEvents(e.data); setMembers(m.data);
    } catch { toast.error("Could not load community"); }
  };
  useEffect(() => { load(); }, []);

  // Session 60.7 · Notification deep-link resolver. Runs after the feed has
  // loaded once. If `?post=<pid>` is present and the target is not already
  // in the feed, fetch it directly from /posts/{pid} and pin it to the
  // top. Then scroll it into view and (optionally) open its comment
  // thread when `?comment=1`. If the post is deleted / hidden / not
  // permitted we render an inline banner instead of a blank page.
  useEffect(() => {
    if (!targetPostId) return;
    if (posts.length === 0) return; // wait for initial load
    const already = posts.find((p) => p.post_id === targetPostId);
    let cancelled = false;
    (async () => {
      if (!already) {
        try {
          const r = await api.get(`/posts/${targetPostId}`);
          if (cancelled) return;
          setPosts((all) => {
            if (all.find((p) => p.post_id === r.data.post_id)) return all;
            return [r.data, ...all];
          });
        } catch (e) {
          if (!cancelled) setTargetMissing(true);
          return;
        }
      }
      // Give React a tick to render the pinned post, then scroll + focus.
      setTimeout(() => {
        if (cancelled) return;
        const el = targetPostRef.current;
        if (el) {
          try { el.scrollIntoView({ behavior: "smooth", block: "center" }); }
          catch { /* jsdom / older browsers */ }
        }
        if (focusComment) setOpenCommentPostId(targetPostId);
      }, 60);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetPostId, posts.length]);

  const submitPost = async () => {
    const text = composer.trim();
    // Session 36 · Milestone C · Phase 3 — allow image-only posts
    if (!text && composerImageIds.length === 0) return;
    // Guard against submitting while uploads are still in flight
    const stillUploading = composerImagePreviews.some(p => p.status === "uploading");
    if (stillUploading) { toast.error("Images still uploading — hang on a sec"); return; }
    setPosting(true);
    try {
      const r = await api.post("/posts", { body: text, image_ids: composerImageIds });
      setPosts((p) => [r.data, ...p]);
      setComposer("");
      setComposerImageIds([]);
      setComposerImagePreviews([]);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not post"); }
    finally { setPosting(false); }
  };

  const toggleLike = async (pid) => {
    setPosts((all) => all.map((p) => p.post_id === pid ? { ...p, liked: !p.liked, likes_count: p.likes_count + (p.liked ? -1 : 1) } : p));
    try { await api.post(`/posts/${pid}/like`); }
    catch { toast.error("Could not update like"); load(); }
  };

  const saveEdit = async (post) => {
    const body = editBody.trim();
    if (!body) { toast.error("Post cannot be empty"); return; }
    setSavingEdit(true);
    try {
      const isAdminEdit = user?.role === "admin" && post.user_id !== user?.user_id;
      const reason = isAdminEdit ? window.prompt("Internal moderation reason (optional, not shown publicly):") || undefined : undefined;
      const r = await api.patch(`/posts/${post.post_id}`, { body, moderation_reason: reason });
      setPosts(all => all.map(p => p.post_id === post.post_id ? r.data : p));
      setEditingPostId(null); setEditBody("");
      toast.success("Post updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update post");
    } finally { setSavingEdit(false); }
  };

  const deletePost = async (post) => {
    setOpenMenu(null);
    const isAdminDelete = user?.role === "admin" && post.user_id !== user?.user_id;
    if (!window.confirm(isAdminDelete
      ? `Delete this post by ${post.author_name}?\n\nAs an admin, this is a soft-delete — the post can be restored from Admin → Moderation.`
      : "Delete your post?\n\nThis hides it from every feed. Admins may still see it in the moderation log."
    )) return;
    const reason = isAdminDelete ? window.prompt("Internal moderation reason (optional, not shown publicly):") || undefined : undefined;
    try {
      const url = reason ? `/posts/${post.post_id}?moderation_reason=${encodeURIComponent(reason)}` : `/posts/${post.post_id}`;
      await api.delete(url);
      setPosts(all => all.filter(p => p.post_id !== post.post_id));
      toast.success("Post removed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete post");
    }
  };

  const toggleRsvp = async (eid) => {
    setEvents((all) => all.map((e) => e.event_id === eid ? { ...e, going: !e.going, rsvp_count: e.rsvp_count + (e.going ? -1 : 1) } : e));
    try { await api.post(`/events/${eid}/rsvp`); }
    catch { toast.error("Could not RSVP"); load(); }
  };

  // Beta punch-list P0-1 · Provider-aware CTA copy for external RSVPs.
  // Only used when registration_type === "external".
  const externalRsvpLabel = (url) => {
    if (!url) return "RSVP on organizer site ↗";
    const u = url.toLowerCase();
    if (u.includes("eventbrite.")) return "RSVP on Eventbrite ↗";
    if (u.includes("meetup.")) return "RSVP on Meetup ↗";
    if (u.includes("lu.ma") || u.includes("luma.")) return "RSVP on Luma ↗";
    return "RSVP on organizer site ↗";
  };
  const handleExternalRsvp = (e, url) => {
    e.preventDefault();
    if (!url) return;
    openExternal(url);
  };

  // Session 59.3 · Comment thread handlers
  const toggleComments = async (pid) => {
    if (openCommentPostId === pid) { setOpenCommentPostId(null); return; }
    setOpenCommentPostId(pid);
    if (commentsByPost[pid]) return;
    try {
      const r = await api.get(`/posts/${pid}/comments`);
      setCommentsByPost(m => ({ ...m, [pid]: r.data.comments || [] }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load comments");
    }
  };
  const submitComment = async (pid) => {
    const text = (commentDraft[pid] || "").trim();
    if (!text) return;
    setCommentBusy(b => ({ ...b, [pid]: true }));
    try {
      const r = await api.post(`/posts/${pid}/comment`, { text });
      const newRow = {
        comment_id: r.data.comment_id,
        post_id: pid,
        author_id: user.user_id,
        author_name: user.name,
        author_avatar_url: user.avatar_url,
        author_role: user.role,
        author_member_type: user.member_type,
        author_premium_lifetime: user.premium_lifetime,
        text,
        created_at: r.data.created_at,
      };
      setCommentsByPost(m => ({ ...m, [pid]: [...(m[pid] || []), newRow] }));
      setCommentDraft(d => ({ ...d, [pid]: "" }));
      setPosts(all => all.map(p => p.post_id === pid ? { ...p, comments_count: (p.comments_count || 0) + 1 } : p));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not comment");
    } finally {
      setCommentBusy(b => ({ ...b, [pid]: false }));
    }
  };
  const deleteComment = async (pid, cid) => {
    if (!window.confirm("Delete this comment?")) return;
    try {
      await api.delete(`/posts/${pid}/comment/${cid}`);
      setCommentsByPost(m => ({ ...m, [pid]: (m[pid] || []).filter(c => c.comment_id !== cid) }));
      setPosts(all => all.map(p => p.post_id === pid ? { ...p, comments_count: Math.max(0, (p.comments_count || 0) - 1) } : p));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete comment");
    }
  };

  if (!user) return null;

  return (
    <AppShell>
      <Toaster richColors position="top-center" />
      <div className="m-community-layout">
        {/* LEFT */}
        <aside className="m-card m-profile-card" data-testid="profile-card">
          {/* Session 42 · MemberAvatar so the user's own profile card in the
              Commons shows the same picture they uploaded (falls back to
              initials automatically when picture is missing). */}
          <MemberAvatar user={user} size={64} clickable={false} />
          <div>
            <div className="m-profile-name">{_displayName(user)}</div>
            <div className="m-profile-meta">{user.city || "—"}</div>
            <div className="m-profile-meta">
              Member since {new Date(user.created_at).toLocaleDateString(undefined, { month: "long", year: "numeric" })}
            </div>
          </div>
          {/* Session 60.7 · Removed the "Founding Member" text chip so the
              profile card does not duplicate the identity signage that lives
              on /profile. Membership status is preserved on the account
              record. */}
          <ul className="m-navlist">
            <li><a className="active" href="#feed"><span>Feed</span><span className="gdot" /></a></li>
            <li><a href="#events">Events</a></li>
            <li><a href="#nearby">Members Nearby</a></li>
          </ul>
        </aside>

        {/* CENTER */}
        <main>
          <ZipPromptBanner />
          <div className="m-card m-composer" data-testid="composer-card">
            <div className="m-composer-title">Share with the <em>Métier Common</em></div>
            <div className="m-composer-row">
              <MemberAvatar user={user} size={40} clickable={false} />
              <textarea
                value={composer}
                onChange={(e) => setComposer(e.target.value)}
                placeholder={`What's on your mind, ${_firstName(user)}?`}
                data-testid="composer-text"
              />
            </div>
            <PostImagePicker
              imageIds={composerImageIds}
              onChange={setComposerImageIds}
              onPreviewsChange={setComposerImagePreviews}
              disabled={posting}
            />
            <div className="m-composer-actions">
              <button
                className="m-btn-gold"
                disabled={posting || (!composer.trim() && composerImageIds.length === 0)}
                onClick={submitPost}
                data-testid="post-btn">
                {posting ? "Posting…" : "Post"}
              </button>
            </div>
          </div>

          {/* Discord references fully removed per launch-readiness audit (Session 39). */}

          <div className="m-feed" id="feed" data-testid="feed">
            {targetMissing && (
              <div
                className="m-card"
                data-testid="target-post-missing"
                style={{ padding: 16, marginBottom: 12, borderColor: "rgba(212,168,75,0.28)" }}
              >
                <div style={{fontSize:13, color:"rgba(255,255,255,0.7)"}}>
                  We could not find that post. It may have been removed by
                  its author or a moderator.
                </div>
              </div>
            )}
            {posts.length === 0 ? (
              <div className="m-empty">The Métier Common awaits its first post.</div>
            ) : posts.map((p) => {
              const canModerate = user && (user.user_id === p.user_id || user.role === "admin");
              const isEditing = editingPostId === p.post_id;
              const isTarget = targetPostId && p.post_id === targetPostId;
              return (
              <article
                key={p.post_id}
                ref={isTarget ? targetPostRef : undefined}
                className="m-card m-post"
                data-testid={`post-${p.post_id}`}
                data-target-post={isTarget ? "true" : undefined}
                style={isTarget ? { boxShadow: "0 0 0 2px rgba(184,145,58,0.55)" } : undefined}
              >
                <div className="m-post-head">
                  <MemberAvatar
                    user={p.author || { user_id: p.user_id, name: p.author_name, picture: p.author_picture, initials: p.author_initials }}
                    size={44}
                  />
                  <div style={{flex:1}}>
                    <div className="m-post-name">
                      <Link
                        to={`/community/members/${p.user_id}`}
                        style={{ color: "inherit", textDecoration: "none" }}
                        data-testid={`post-author-link-${p.post_id}`}
                      >{_displayName({ display_name: p.author?.display_name, name: p.author_name })}</Link>
                      {/* Session 60.7 · Badges removed from the Commons feed
                          per Cam's cleanup pass. Membership status stays on
                          the member's profile page. */}
                    </div>
                    <div className="m-post-meta">
                      {p.author_city || "—"} · {timeAgo(p.created_at)}
                      {p.edited_at && (
                        <span className="m-post-edited" title={`Edited ${new Date(p.edited_at).toLocaleString()}`}> · Edited</span>
                      )}
                    </div>
                  </div>
                  {canModerate && !isEditing && (
                    <div className="m-post-menu" data-testid={`post-menu-${p.post_id}`}>
                      <button
                        className="m-post-menu-btn"
                        onClick={() => setOpenMenu(openMenu === p.post_id ? null : p.post_id)}
                        aria-label="Post options"
                        data-testid={`post-menu-btn-${p.post_id}`}
                      >⋯</button>
                      {openMenu === p.post_id && (
                        <div className="m-post-menu-dropdown">
                          <button onClick={() => { setEditingPostId(p.post_id); setEditBody(p.body); setOpenMenu(null); }}
                            data-testid={`post-edit-${p.post_id}`}>Edit post</button>
                          <button onClick={() => deletePost(p)} className="danger"
                            data-testid={`post-delete-${p.post_id}`}>Delete post</button>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {isEditing ? (
                  <div className="m-post-edit-form">
                    <textarea
                      value={editBody}
                      onChange={e => setEditBody(e.target.value)}
                      rows={4}
                      data-testid={`post-edit-textarea-${p.post_id}`}
                      maxLength={1000}
                    />
                    <div className="m-post-edit-actions">
                      <button className="m-btn-ghost small" onClick={() => { setEditingPostId(null); setEditBody(""); }}
                        data-testid={`post-edit-cancel-${p.post_id}`}>Cancel</button>
                      <button className="m-btn-gold small" onClick={() => saveEdit(p)} disabled={!editBody.trim() || savingEdit}
                        data-testid={`post-edit-save-${p.post_id}`}>{savingEdit ? "Saving…" : "Save changes"}</button>
                    </div>
                  </div>
                ) : (
                  <>
                    {p.hours_location && (
                      <div
                        className="m-post-hours-header"
                        data-testid={`post-hours-header-${p.post_id}`}
                        style={{ marginBottom: 10, display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}
                      >
                        <span style={{ color: "rgba(255,255,255,0.82)", fontSize: 13.5, lineHeight: 1.5 }}>Worked from</span>
                        <HoursLocationLink
                          cafeId={p.hours_location.cafe_id}
                          name={p.hours_location.name}
                          testid={`post-hours-cafe-link-${p.post_id}`}
                        />
                        {(p.hours_location.city || p.hours_location.state) && (
                          <span style={{ color: "rgba(255,255,255,0.55)", fontSize: 12.5 }}>
                            {[p.hours_location.city, p.hours_location.state].filter(Boolean).join(", ")}
                          </span>
                        )}
                      </div>
                    )}
                    <div className="m-post-body" style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{linkify(p.body)}</div>
                  </>
                )}

                {/* Milestone C · Phase 3 — attached images */}
                {p.images && p.images.length > 0 && (
                  <PostImages images={p.images} />
                )}

                <div className="m-post-actions">
                  <button className={p.liked ? "liked" : ""} onClick={() => toggleLike(p.post_id)} data-testid={`like-btn-${p.post_id}`}>
                    <HeartIcon filled={p.liked} /> <span>{p.likes_count} {p.likes_count === 1 ? "like" : "likes"}</span>
                  </button>
                  <button
                    onClick={() => toggleComments(p.post_id)}
                    className={openCommentPostId === p.post_id ? "liked" : ""}
                    data-testid={`comment-btn-${p.post_id}`}>
                    <CommentIcon /> <span>{p.comments_count || 0} {(p.comments_count || 0) === 1 ? "comment" : "comments"}</span>
                  </button>
                </div>

                {openCommentPostId === p.post_id && (
                  <div className="m-comments" data-testid={`comments-thread-${p.post_id}`}
                    style={{marginTop:12, paddingTop:12, borderTop:"1px solid rgba(212,168,75,0.14)"}}>
                    {(commentsByPost[p.post_id] || []).map(c => (
                      <div key={c.comment_id} className="m-comment"
                        data-testid={`comment-${c.comment_id}`}
                        style={{display:"flex", gap:10, padding:"8px 0", alignItems:"flex-start"}}>
                        {/* Session 60.2 · P0.3 Comment avatar — read canonical
                            profile_image_url from server; make clickable to
                            actor's profile. MemberAvatar handles broken-image
                            fallback + initials internally. */}
                        <MemberAvatar
                          user={{
                            user_id: c.author_id,
                            display_name: c.author_name,
                            name: c.author_name,
                            profile_image_url: c.author_profile_image_url || c.author_avatar_url,
                            initials: c.author_initials,
                          }}
                          size={28}
                        />
                        <div style={{flex:1, minWidth:0}}>
                          <div style={{display:"flex", alignItems:"baseline", gap:8, flexWrap:"wrap"}}>
                            {/* Session 60.2 · P1.4 clickable member name */}
                            {c.author_id ? (
                              <Link
                                to={`/community/members/${c.author_id}`}
                                data-testid={`comment-author-link-${c.comment_id}`}
                                style={{ fontSize:13, fontWeight:600, color:"inherit", textDecoration:"none" }}
                              >{_displayName({ name: c.author_name })}</Link>
                            ) : (
                              <strong style={{fontSize:13}}>{_displayName({ name: c.author_name })}</strong>
                            )}
                            {/* Session 60.7 · Badges removed from Commons
                                comments per Cam's cleanup pass. */}
                            <span style={{fontSize:11, color:"rgba(255,255,255,0.5)"}}>{timeAgo(c.created_at)}</span>
                            {(c.author_id === user.user_id || user.role === "admin") && (
                              <button onClick={() => deleteComment(p.post_id, c.comment_id)}
                                data-testid={`comment-delete-${c.comment_id}`}
                                style={{background:"none", border:"none", color:"rgba(255,255,255,0.4)", fontSize:11, cursor:"pointer", marginLeft:"auto"}}>
                                Delete
                              </button>
                            )}
                          </div>
                          <div style={{fontSize:14, marginTop:2, whiteSpace:"pre-wrap", wordBreak:"break-word"}}>{linkify(c.text)}</div>
                        </div>
                      </div>
                    ))}
                    {(commentsByPost[p.post_id] || []).length === 0 && (
                      <div style={{fontSize:12, color:"rgba(255,255,255,0.45)", padding:"4px 0 8px"}}>Be the first to comment.</div>
                    )}
                    <div style={{display:"flex", gap:8, marginTop:8}}>
                      <textarea
                        value={commentDraft[p.post_id] || ""}
                        onChange={e => setCommentDraft(d => ({ ...d, [p.post_id]: e.target.value }))}
                        placeholder="Write a comment…"
                        rows={2}
                        maxLength={1000}
                        data-testid={`comment-input-${p.post_id}`}
                        style={{flex:1, background:"var(--navy-mid)", color:"#fff", border:"1px solid rgba(212,168,75,0.22)", borderRadius:8, padding:"8px 10px", fontSize:13, resize:"vertical", fontFamily:"inherit"}}
                      />
                      <button
                        onClick={() => submitComment(p.post_id)}
                        disabled={commentBusy[p.post_id] || !(commentDraft[p.post_id] || "").trim()}
                        data-testid={`comment-submit-${p.post_id}`}
                        className="m-btn-gold"
                        style={{alignSelf:"flex-end", padding:"8px 14px", fontSize:12, opacity: commentBusy[p.post_id] ? 0.6 : 1}}>
                        {commentBusy[p.post_id] ? "…" : "Post"}
                      </button>
                    </div>
                  </div>
                )}
              </article>
              );
            })}
          </div>
        </main>

        {/* RIGHT */}
        <aside className="m-side-col">
          <div className="m-card" id="events" data-testid="events-card">
            <h4 className="m-side-title">Upcoming <em>events</em></h4>
            {/* Session 60.11 · Submit-an-event CTA lives here so members
                see it while browsing the feed. Landing page: /events/submit
                — pipeline writes status=pending for admin review. */}
            <Link
              to="/events/submit"
              data-testid="community-submit-event-cta"
              style={{
                display: "block",
                marginBottom: 12,
                padding: "10px 12px",
                borderRadius: 8,
                background: "rgba(212,168,75,0.10)",
                border: "1px dashed rgba(212,168,75,0.45)",
                color: "var(--gold, #B8913A)",
                fontSize: 12.5,
                fontWeight: 600,
                letterSpacing: "0.02em",
                textDecoration: "none",
                textAlign: "center",
              }}
            >
              + Have an event? Submit it for promotion &rarr;
            </Link>
            <div>
              {events.map((e) => {
                // Beta punch-list P0-1 · Route RSVPs based on registration_type:
                //   metier   → internal RSVP toggle (unchanged)
                //   external → open the validated external registration_url
                //              (Eventbrite, Meetup, Luma, etc.) in the system
                //              browser. Never create a Métier RSVP record.
                const isExternal = e.registration_type === "external";
                const externalUrl = isExternal ? e.registration_url : null;
                const showCta = !isExternal || Boolean(externalUrl);
                return (
                  <div key={e.event_id} className="m-event" data-testid={`event-${e.event_id}`}>
                    <div className="m-event-date">{e.date}</div>
                    <div className="m-event-title">{e.title}</div>
                    <div className="m-event-loc">{e.location}</div>
                    <div className="m-event-rsvp">
                      {showCta && (isExternal ? (
                        <a
                          href={externalUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(ev) => handleExternalRsvp(ev, externalUrl)}
                          className="m-btn-ghost"
                          data-testid={`rsvp-external-${e.event_id}`}
                        >
                          {externalRsvpLabel(externalUrl)}
                        </a>
                      ) : (
                        <button
                          className={"m-btn-ghost" + (e.going ? " going" : "")}
                          onClick={() => toggleRsvp(e.event_id)}
                          data-testid={`rsvp-btn-${e.event_id}`}
                        >
                          {e.going ? "Going ✓" : "RSVP"}
                        </button>
                      ))}
                      {e.rsvp_count > 0 && <span className="m-rsvp-count">{e.rsvp_count} going</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="m-card mt-20" id="nearby" data-testid="nearby-card">
            <h4 className="m-side-title">Members <em>near you</em></h4>
            <div>
              {members.map((m) => (
                <div key={m.user_id} className="m-member-row" data-testid={`nearby-member-${m.user_id}`}>
                  {/* Session 42 · MemberAvatar — nearby-members list */}
                  <MemberAvatar user={m} size={32} clickable={true} />
                  <div>
                    <span className="m-member-name">{_displayName(m)}</span>
                    <span className="m-member-city">
                      {/* Session 60.20 · Distance-first Members Near You.
                          Prefer resolved City, ST from ZIP; fall back to
                          legacy freeform city; suffix distance when the
                          backend scored the row via Tier 1. */}
                      {(m.resolved_city && m.state_code) ? `${m.resolved_city}, ${m.state_code}` : (m.city || "—")}
                      {typeof m.distance_miles === "number" && (
                        <span
                          data-testid={`nearby-distance-${m.user_id}`}
                          style={{ marginLeft: 8, color: "var(--gold, #B8913A)", fontWeight: 600 }}
                        >
                          {m.distance_miles < 1 ? "< 1" : Math.round(m.distance_miles)} mi
                        </span>
                      )}
                    </span>
                  </div>
                </div>
              ))}
              {members.length === 0 && (
                <div className="m-muted small" data-testid="nearby-empty">
                  Add your ZIP on your profile to see members near you.
                </div>
              )}
            </div>
          </div>
        </aside>
      </div>
    </AppShell>
  );
}
